import React, { useState, useEffect } from 'react';
import { FiX, FiEdit, FiLoader, FiStar, FiCalendar, FiCreditCard, FiTag } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';
import { fetchJson } from '../../utils/fetchJson';

const EditUserModal = ({ isOpen, onClose, user, onUserUpdated }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gender: 'male',
        membershipType: 'free',
        subscription: {
            plan: 'premium_monthly',
            source: 'google_play',
            status: 'active',
            orderId: '',
            productId: 'com.marrycenter.premium.monthly',
            purchaseDate: '',
            expiryDate: '',
            amount: 0,
            currency: 'USD',
            autoRenewing: true,
            notes: ''
        }
    });
    const [error, setError] = useState('');

    useEffect(() => {
        if (user) {
            const sub = user.subscription || {};
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                phone: user.phone || '',
                gender: user.gender || 'male',
                membershipType: user.membershipType || (sub.status === 'active' ? 'premium' : 'free'),
                subscription: {
                    plan: sub.plan || 'premium_monthly',
                    source: sub.source || 'google_play',
                    status: sub.status || 'active',
                    orderId: sub.orderId || '',
                    productId: sub.productId || 'com.marrycenter.premium.monthly',
                    purchaseDate: sub.purchaseDate ? new Date(sub.purchaseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                    expiryDate: sub.expiryDate ? new Date(sub.expiryDate).toISOString().split('T')[0] : '',
                    amount: sub.amount || 0,
                    currency: sub.currency || 'USD',
                    autoRenewing: sub.autoRenewing !== undefined ? sub.autoRenewing : true,
                    notes: sub.notes || ''
                }
            });
        }
    }, [user]);

    if (!isOpen || !user) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            subscription: {
                ...prev.subscription,
                [name]: type === 'checkbox' ? checked : value
            }
        }));
    };

    const handleMembershipChange = (e) => {
        const newType = e.target.value;
        setFormData(prev => ({
            ...prev,
            membershipType: newType,
            subscription: {
                ...prev.subscription,
                status: newType === 'free' ? 'expired' : 'active'
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const userId = user._id || user.id;

            const payload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                gender: formData.gender,
                membershipType: formData.membershipType,
                subscription: formData.membershipType === 'free' ? {
                    ...formData.subscription,
                    status: 'none',
                    source: 'none'
                } : {
                    ...formData.subscription,
                    status: formData.subscription.status || 'active'
                }
            };

            const { response, data } = await fetchJson(buildApiUrl(`/api/admin/users/${userId}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok && data.success) {
                if (onUserUpdated) {
                    onUserUpdated(data.user || data.data || { ...user, ...payload });
                }
                onClose();
            } else {
                setError(data.message || 'Failed to update user');
            }
        } catch (err) {
            console.error('Error updating user:', err);
            setError('An error occurred while updating the user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto shadow-2xl">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition"
                >
                    <FiX className="w-5 h-5" />
                </button>

                <div className="flex items-center space-x-2 mb-6 border-b pb-4">
                    <div className="p-2 bg-red-50 text-red-600 rounded-lg">
                        <FiEdit className="w-5 h-5" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Edit User & Membership</h2>
                        <p className="text-xs text-gray-500">Update account profile and Google Play subscription details</p>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm border border-red-200">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Section 1: Basic Information */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Basic Information</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-lg px-3 py-2 text-sm cursor-not-allowed"
                                    value={formData.email}
                                    disabled
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                            <select
                                name="gender"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                value={formData.gender}
                                onChange={handleChange}
                            >
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>
                    </div>

                    {/* Section 2: Membership & Google Play Subscription */}
                    <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                                <FiStar className="text-yellow-500" />
                                Membership & Google Play Settings
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Membership Plan Tier</label>
                                <select
                                    name="membershipType"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 font-semibold"
                                    value={formData.membershipType}
                                    onChange={handleMembershipChange}
                                >
                                    <option value="free">Free Member</option>
                                    <option value="premium">⭐ Premium Member</option>
                                    <option value="vip">👑 VIP Member</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Payment / Subscription Source</label>
                                <select
                                    name="source"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                    value={formData.subscription.source}
                                    onChange={handleSubChange}
                                    disabled={formData.membershipType === 'free'}
                                >
                                    <option value="google_play">Google Play Store (Mobile App In-App Purchase)</option>
                                    <option value="manual_admin">Admin Manual Assignment</option>
                                    <option value="apple_store">Apple App Store</option>
                                    <option value="stripe">Stripe / Web Checkout</option>
                                    <option value="other">Other / Direct</option>
                                </select>
                            </div>
                        </div>

                        {formData.membershipType !== 'free' && (
                            <div className="mt-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                                            <FiTag className="text-gray-500" />
                                            Google Play Order ID / Transaction ID
                                        </label>
                                        <input
                                            type="text"
                                            name="orderId"
                                            placeholder="e.g. GPA.3344-5566-7788-99000"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500 font-mono"
                                            value={formData.subscription.orderId}
                                            onChange={handleSubChange}
                                        />
                                        <span className="text-[11px] text-gray-500">Order ID from Google Play Payment Profile</span>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                                            <FiCreditCard className="text-gray-500" />
                                            Subscription Status
                                        </label>
                                        <select
                                            name="status"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                            value={formData.subscription.status}
                                            onChange={handleSubChange}
                                        >
                                            <option value="active">Active</option>
                                            <option value="expired">Expired</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                                            <FiCalendar className="text-gray-500" />
                                            Purchase / Start Date
                                        </label>
                                        <input
                                            type="date"
                                            name="purchaseDate"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                            value={formData.subscription.purchaseDate}
                                            onChange={handleSubChange}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                                            <FiCalendar className="text-gray-500" />
                                            Expiry / Renewal Date
                                        </label>
                                        <input
                                            type="date"
                                            name="expiryDate"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                            value={formData.subscription.expiryDate}
                                            onChange={handleSubChange}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        Admin Notes
                                    </label>
                                    <input
                                        type="text"
                                        name="notes"
                                        placeholder="e.g., Verified Google Play Order"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                        value={formData.subscription.notes}
                                        onChange={handleSubChange}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition flex items-center shadow-sm disabled:opacity-50"
                        >
                            {loading && <FiLoader className="animate-spin mr-2" />}
                            Save & Update User
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUserModal;
